import posthog from 'posthog-js';
import { captureAnalytics } from '../actions/preload';
import { captureAnalyticsOncePerSession } from '../actions/helpers/sessionAnalytics';

const canTrack = () => (
  process.env.NODE_ENV !== 'development'
  && process.env.NODE_ENV !== 'test'
  && posthog
  && typeof posthog.capture === 'function'
);

export const trackReferralActionClicked = ({
  action,
  placement = 'unknown',
  selectedCount = 0,
  selectedAll = false,
  isArchivedView = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('referral_action_clicked', {
    action,
    placement,
    selectedCount,
    selectedAll,
    isArchivedView,
  });
};

// Auto Split tracking

export const trackAutoSplitBannerShown = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_banner_shown', {
    sendToken,
    totalPages,
    partsCount,
  });
};

export const trackAutoSplitBannerReviewClicked = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_banner_review_clicked', {
    sendToken,
    totalPages,
    partsCount,
  });
};

export const trackAutoSplitBannerDismissed = ({
  sendToken,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_banner_dismissed', {
    sendToken,
  });
};

export const trackAutoSplitModalClosed = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  selectedCount = 0,
  hadEdits = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_modal_closed', {
    sendToken,
    totalPages,
    partsCount,
    selectedCount,
    hadEdits,
  });
};

export const trackAutoSplitExecuted = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  selectedCount = 0,
  splitMode,
  hadEdits = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_executed', {
    sendToken,
    totalPages,
    partsCount,
    selectedCount,
    splitMode,
    hadEdits,
  });
};

export const trackAutoSplitSucceeded = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  selectedCount = 0,
  splitMode,
  hadEdits = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_succeeded', {
    sendToken,
    totalPages,
    partsCount,
    selectedCount,
    splitMode,
    hadEdits,
  });
};

export const trackAutoSplitFailed = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  selectedCount = 0,
  splitMode,
  hadEdits = false,
  error,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_failed', {
    sendToken,
    totalPages,
    partsCount,
    selectedCount,
    splitMode,
    hadEdits,
    error,
  });
};

export const trackAutoSplitPartAdded = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_part_added', {
    sendToken,
    totalPages,
    partsCount,
  });
};

export const trackAutoSplitPartRemoved = ({
  sendToken,
  totalPages = 0,
  partsCount = 0,
  removedIndex = 0,
}) => {
  if (!canTrack()) return;

  posthog.capture('auto_split_part_removed', {
    sendToken,
    totalPages,
    partsCount,
    removedIndex,
  });
};

export const trackReferralFilterChanged = ({
  filterKey,
  operation,
  source = 'unknown',
  valueCount = 0,
  hasValue = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('referral_filter_changed', {
    filterKey,
    operation,
    source,
    valueCount,
    hasValue,
  });
};

export const trackInboxFilterChanged = ({
  filterKey,
  operation,
  source = 'unknown',
  valueCount = 0,
  hasValue = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('inbox_filter_changed', {
    filterKey,
    operation,
    source,
    valueCount,
    hasValue,
  });
};

// Referral template management tracking

export const trackReferralTemplateEvent = ({
  action,
  ...props
}) => {
  if (!canTrack()) return;

  posthog.capture('referral_template_event', {
    action,
    ...props,
  });
};

// Recapture upsell tracking

const recaptureActivationFailureType = (error = '') => {
  const message = String(error || '').toLowerCase();

  if (message.includes('invalid tier')) return 'invalid_tier';
  if (message.includes('only administrators')) return 'admin_required';
  if (message.includes('not available')) return 'upsell_unavailable';
  if (message.includes('already activated')) return 'already_active';
  if (message.includes('activation failed')) return 'request_failed';

  return 'unknown';
};

export const trackRecaptureUpsellViewed = ({
  hasProductAccess = false,
  isAdmin = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_viewed', {
    hasProductAccess,
    isAdmin,
  });
};

export const trackRecaptureUpsellCtaClicked = ({
  cta,
  placement = 'unknown',
  tier = null,
}) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_cta_clicked', {
    cta,
    placement,
    tier,
  });
};

export const trackRecaptureUpsellActivationSubmitted = ({ tier }) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_activation_submitted', { tier });
};

export const trackRecaptureUpsellActivationSucceeded = ({ tier }) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_activation_succeeded', { tier });
};

export const trackRecaptureUpsellActivationFailed = ({ tier, error }) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_activation_failed', {
    tier,
    errorType: recaptureActivationFailureType(error),
  });
};

export const trackRecaptureUpsellFlashClicked = ({ placement = 'nav' } = {}) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_flash_clicked', { placement });
};

export const trackRecaptureUpsellFlashDismissed = ({ placement = 'nav' } = {}) => {
  if (!canTrack()) return;

  posthog.capture('recapture_upsell_flash_dismissed', { placement });
};

// ARM (Autonomous Referral Management) upsell tracking

const armSetupRequestFailureType = (error = '') => {
  const message = String(error || '').toLowerCase();

  if (message.includes('invalid placement')) return 'invalid_placement';
  if (message.includes('only administrators')) return 'admin_required';
  if (message.includes('not available')) return 'upsell_unavailable';
  if (message.includes('already enabled')) return 'already_active';
  if (message.includes('network error')) return 'network_error';

  return 'unknown';
};

export const trackArmUpsellViewed = ({
  hasProductAccess = false,
  isAdmin = false,
}) => {
  if (!canTrack()) return;

  posthog.capture('arm_upsell_viewed', {
    hasProductAccess,
    isAdmin,
  });
};

// Every "Get Started Today" click, before the booking page opens. `placement`
// says which button (hero, closing_cta).
export const trackArmUpsellGetStartedClicked = ({ placement = 'unknown' }) => {
  if (!canTrack()) return;

  posthog.capture('arm_upsell_get_started_clicked', { placement });
};

// The server accepted the lead ping (whether or not Slack was actually sent
// this window — `notified` carries that).
export const trackArmUpsellSetupRequested = ({ placement = 'unknown', notified = false }) => {
  if (!canTrack()) return;

  posthog.capture('arm_upsell_setup_requested', { placement, notified });
};

export const trackArmUpsellSetupRequestFailed = ({ placement = 'unknown', error }) => {
  if (!canTrack()) return;

  posthog.capture('arm_upsell_setup_request_failed', {
    placement,
    errorType: armSetupRequestFailureType(error),
  });
};

export const trackArmUpsellFlashClicked = ({ placement = 'nav' } = {}) => {
  if (!canTrack()) return;

  posthog.capture('arm_upsell_flash_clicked', { placement });
};

// Patient Engagement upsell tracking. These go through captureAnalytics, which
// swallows SDK errors, because the click handler calls them before it opens the
// booking page and pings sales: a PostHog failure must never cancel either.
// Error strings come from Api::V1::PatientEngagementSetupRequestsController,
// which uses the same wording as the ARM endpoint.

// Passive (fires on render), so it is counted once per tab session.
export const trackPatientEngagementUpsellViewed = ({
  hasProductAccess = false,
  isAdmin = false,
}) => {
  captureAnalyticsOncePerSession('patient_engagement_upsell_viewed', {
    hasProductAccess,
    isAdmin,
  });
};

// Every "Get Started Today" click, before the booking page opens. `placement`
// says which button (hero, closing_cta).
export const trackPatientEngagementUpsellGetStartedClicked = ({ placement = 'unknown' }) => {
  captureAnalytics('patient_engagement_upsell_get_started_clicked', { placement });
};

// The browser blocked the booking tab, so the page showed a link instead.
export const trackPatientEngagementUpsellPopupBlocked = ({ placement = 'unknown' }) => {
  captureAnalytics('patient_engagement_upsell_popup_blocked', { placement });
};

export const trackPatientEngagementUpsellSetupRequested = ({ placement = 'unknown', notified = false }) => {
  captureAnalytics('patient_engagement_upsell_setup_requested', { placement, notified });
};

export const trackPatientEngagementUpsellSetupRequestFailed = ({ placement = 'unknown', error }) => {
  captureAnalytics('patient_engagement_upsell_setup_request_failed', {
    placement,
    errorType: armSetupRequestFailureType(error),
  });
};

export const trackPatientEngagementUpsellFlashClicked = ({ placement = 'nav' } = {}) => {
  captureAnalytics('patient_engagement_upsell_flash_clicked', { placement });
};
